// The PC user menu (≥1024px; below that the avatar opens the Account sheet). The avatar, name and role with
// a chevron open a small menu holding what used to sit loose in the bar beside them: Settings, Tickets
// (your tickets to the administrators, in Settings), Administration (only for roles holding an admin permission, the same rule as before), the colour profile
// and Log out.
//
// A WAI-ARIA menu button. Enter, Space or ArrowDown on the button open the menu on its first item, ArrowUp
// on its last. Inside, Up / Down / Home / End move between the rows (the swatch row counts as one, entered
// on the chosen colour), Left / Right move along the swatches, Enter / Space choose. Escape closes and hands
// focus back to the button; Tab, a click outside or a change of page close it.
import { useEffect, useId, useRef, useState, type CSSProperties, type KeyboardEvent } from 'react';
import { useLocation } from 'react-router-dom';
import { roleLabel, TICKETS_LINK } from '@technohull/shared';
import { useAuth } from '../auth/AuthContext.js';
import { useSettings } from '../settings/SettingsContext.js';
import { ADMIN_PERMS } from '../app/nav.js';
import { PALETTES, PALETTE_ORDER, paletteId } from '../lib/palettes.js';
import { Avatar } from './Avatar.js';

// 24×24, 1.75 stroke, round caps: the rail's icon language
const Glyph = ({ d, className }: { d: string; className?: string }) => (
  <svg className={className} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    <path d={d} />
  </svg>
);
const SETTINGS = 'M4 7h9M17 7h3M4 17h3M11 17h9M15 5v4M9 15v4';
const SHIELD = 'M12 21s7-3.5 7-9V5.5L12 3 5 5.5V12c0 5.5 7 9 7 9z';
const TICKET = 'M4 6h16v3.5a2.5 2.5 0 0 0 0 5V18H4v-3.5a2.5 2.5 0 0 0 0-5zM14.5 6v2.5M14.5 11v2M14.5 15.5V18';
const LOG_OUT = 'M10 4H5v16h5M15 8l4 4-4 4M19 12H9';
const CHEVRON = 'M6 9.5l6 6 6-6';

export function UserMenu({ onNavigate }: {
  /** leave the menu for a page: AppShell's overlay-aware navigate (on PC a plain close-then-navigate). */
  onNavigate: (path: string, close: () => void) => void;
}) {
  const { me, can, logout } = useAuth();
  const { prefs, setPalette, uiTheme } = useSettings();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const focusOnOpen = useRef<'first' | 'last'>('first');
  const menuId = useId();
  const colourHd = useId();
  const current = paletteId(prefs.palette);

  const close = (refocus = false) => {
    setOpen(false);
    if (refocus) btnRef.current?.focus();
  };

  // the row stops for Up / Down: every item, with the swatch row entered on its chosen swatch
  const stops = () => Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[data-stop]') ?? []);

  // on open, focus lands on the first (or, from ArrowUp, the last) row
  useEffect(() => {
    if (!open) return;
    const s = stops();
    (focusOnOpen.current === 'last' ? s[s.length - 1] : s[0])?.focus();
  }, [open]);

  // a press anywhere outside closes it, without stealing focus back
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [open]);

  // a new page (a link elsewhere, Back) closes it
  useEffect(() => { setOpen(false); }, [pathname]);

  if (!me) return null;
  const isAdmin = ADMIN_PERMS.some((p) => can(p));
  const name = me.user.displayName;
  const role = roleLabel(me.user.role);

  const onButtonKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      focusOnOpen.current = e.key === 'ArrowUp' ? 'last' : 'first';
      if (open) { const s = stops(); (e.key === 'ArrowUp' ? s[s.length - 1] : s[0])?.focus(); }
      else setOpen(true);
    }
  };

  const onMenuKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const k = e.key;
    if (k === 'Escape') { e.preventDefault(); close(true); return; }
    if (k === 'Tab') { setOpen(false); return; } // focus moves on from the button's place in the order
    const active = document.activeElement as HTMLElement | null;
    const swatch = active?.closest<HTMLElement>('.umenu-sw');
    if ((k === 'ArrowLeft' || k === 'ArrowRight') && swatch) {
      e.preventDefault();
      const sws = Array.from(menuRef.current?.querySelectorAll<HTMLElement>('.umenu-sw') ?? []);
      const i = sws.indexOf(swatch);
      sws[(i + (k === 'ArrowRight' ? 1 : -1) + sws.length) % sws.length]?.focus();
      return;
    }
    if (k !== 'ArrowDown' && k !== 'ArrowUp' && k !== 'Home' && k !== 'End') return;
    e.preventDefault();
    const s = stops();
    if (!s.length) return;
    // a swatch other than the chosen one counts as the swatch row's stop
    const here = swatch ? s.findIndex((el) => el.classList.contains('umenu-sw')) : s.indexOf(active as HTMLElement);
    const j = k === 'Home' ? 0 : k === 'End' ? s.length - 1
      : (here + (k === 'ArrowDown' ? 1 : -1) + s.length) % s.length;
    s[j]?.focus();
  };

  const go = (path: string) => onNavigate(path, () => setOpen(false));

  return (
    <div className="umenu" ref={wrapRef}>
      <button
        ref={btnRef}
        type="button"
        className="user user-btn umenu-btn"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label={`Account: ${name}`}
        title="Account"
        onClick={() => { focusOnOpen.current = 'first'; setOpen((o) => !o); }}
        onKeyDown={onButtonKey}
      >
        <Avatar name={name} avatar={me.user.avatar} size={30} />
        <span className="umenu-who">
          <span className="umenu-name">{name}</span>
          <span className="sub">{role}</span>
        </span>
        <Glyph d={CHEVRON} className="umenu-chev" />
      </button>

      {open && (
        <div className="umenu-pop">
          {/* who is signed in: plain text above the menu proper (a role=menu may only hold items) */}
          <div className="umenu-id">
            <Avatar name={name} avatar={me.user.avatar} size={36} />
            <div className="umenu-id-main">
              <div className="umenu-id-name">{name}</div>
              <div className="umenu-id-meta">{role} · {me.user.email}</div>
            </div>
          </div>
          <div ref={menuRef} id={menuId} role="menu" aria-label="Account" className="umenu-list" onKeyDown={onMenuKey}>
            <button
              type="button" role="menuitem" tabIndex={-1} data-stop className="umenu-item"
              aria-current={pathname === '/settings' ? 'page' : undefined}
              onClick={() => go('/settings')}
            >
              <Glyph d={SETTINGS} className="umenu-ic" />Settings
            </button>
            <button
              type="button" role="menuitem" tabIndex={-1} data-stop className="umenu-item"
              onClick={() => go(TICKETS_LINK)}
            >
              <Glyph d={TICKET} className="umenu-ic" />Tickets
            </button>
            {isAdmin && (
              <button
                type="button" role="menuitem" tabIndex={-1} data-stop className="umenu-item"
                aria-current={pathname.startsWith('/admin') ? 'page' : undefined}
                onClick={() => go('/admin')}
              >
                <Glyph d={SHIELD} className="umenu-ic" />Administration
              </button>
            )}
            {/* a theme with its own colours (Stegi) has no colour profile to pick */}
            {uiTheme.palette === 'full' && (<>
            <div role="separator" className="umenu-sep" />
            <div role="group" aria-labelledby={colourHd} className="umenu-colour">
              <div className="umenu-colour-hd">
                <span id={colourHd}>Colour</span>
                <span className="umenu-colour-cur" aria-hidden="true">{PALETTES[current].label}</span>
              </div>
              <div className="umenu-swatches">
                {PALETTE_ORDER.map((id) => {
                  const p = PALETTES[id];
                  const on = id === current;
                  return (
                    <button
                      key={id}
                      type="button"
                      role="menuitemradio"
                      aria-checked={on}
                      aria-label={p.label}
                      title={`${p.label} · ${p.blurb}`}
                      tabIndex={-1}
                      data-stop={on ? '' : undefined}
                      className="umenu-sw"
                      style={{ '--sw-a': p.swatch, '--sw-b': p.accent } as CSSProperties}
                      // the menu stays open, so another colour is one more press away
                      onClick={(e) => { setPalette(id); e.currentTarget.focus(); }}
                    />
                  );
                })}
              </div>
            </div>
            </>)}
            <div role="separator" className="umenu-sep" />
            <button
              type="button" role="menuitem" tabIndex={-1} data-stop className="umenu-item"
              onClick={() => { setOpen(false); void logout(); }}
            >
              <Glyph d={LOG_OUT} className="umenu-ic" />Log out
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
